import { Injectable } from '@angular/core'
import { Observable, of } from 'rxjs'
import { FlexPositionsQueryRequest } from '../models/flex-positions-query.model'
import { SourceKey } from '../models/source-key.model'
import { WorkbenchRow } from '../models/workbench-row.model'

/** One block of rows, in the shape ag-Grid's `successCallback(rows, lastRow)` needs. */
export interface WorkbenchQueryResult {
  rows: WorkbenchRow[]
  /**
   * The TRUE total row count, known only once a returned block is shorter than the one that was
   * requested — the response carries no total, so a full block tells us nothing about what follows.
   * `undefined` means "still unknown", which ag-Grid reads as -1 and keeps scrolling against.
   */
  lastRow?: number
}

/**
 * Seam between the workbench UI and the Flex Positions read API, mirroring Flex Transactions'
 * identical seam.
 *
 * Now block-oriented rather than page-oriented (PVAL-26423): the caller hands over a fully-built
 * request — including its `startRow`/`endRow` window, filter map and sort list — and gets one block
 * back. The translation from ag-Grid's models into that request lives in
 * `grid/query/query-request.builder.ts`, deliberately outside this seam, so it can be tested against
 * the request body without a transport.
 *
 * Clearwater has no read endpoint, so it resolves to no rows, the same shape as its empty catalog.
 */
export abstract class WorkbenchDataSource {
  /**
   * Fetch one block of rows for a source.
   *
   * @param source which grid source to query — decides the `?source=` query parameter
   * @param request the fully-built request body
   */
  abstract query(
    source: SourceKey,
    request: FlexPositionsQueryRequest,
  ): Observable<WorkbenchQueryResult>

  /**
   * Re-read exactly the named rows for one source, for a targeted refresh after a match action.
   *
   * Unlike {@link query} this takes no readiness gate: the ids come from a server event naming rows
   * that are already rendered, so their catalog has resolved by definition. An empty `rowIds` must
   * issue no request at all — an event naming nothing (or nothing this grid holds) is a no-op, not
   * an empty query.
   *
   * @param source which grid source the ids belong to
   * @param rowIds the row ids to re-read
   */
  abstract loadByIds(
    source: SourceKey,
    rowIds: string[],
  ): Observable<WorkbenchRow[]>
}

/** Trivial data source for Clearwater (no endpoint) and for tests that don't care about rows. */
@Injectable()
export class EmptyWorkbenchDataSource extends WorkbenchDataSource {
  query(): Observable<WorkbenchQueryResult> {
    return of<WorkbenchQueryResult>({ rows: [], lastRow: 0 })
  }

  loadByIds(): Observable<WorkbenchRow[]> {
    return of([])
  }
}
